import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { NAV_GROUPS, hasAccess } from '../../lib/navConfig';

export function SidebarNav({ onNavigate, userRole, moduleToggles, unreadByPath = {} }) {
  const location = useLocation();

  // Persist expanded/collapsed state per group in localStorage
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('pharmxai_nav_expanded');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  // Accordion behavior: opening a group automatically collapses every other group.
  const toggleGroup = (groupId) => {
    setExpanded(prev => {
      const next = prev[groupId] ? {} : { [groupId]: true };
      localStorage.setItem('pharmxai_nav_expanded', JSON.stringify(next));
      return next;
    });
  };

  // Auto-expand the group containing the active route, collapsing all others.
  useEffect(() => {
    for (const group of NAV_GROUPS) {
      if (group.items.some(item => location.pathname === item.path)) {
        setExpanded(prev => {
          const keys = Object.keys(prev);
          if (keys.length === 1 && prev[group.id]) return prev;
          const next = { [group.id]: true };
          localStorage.setItem('pharmxai_nav_expanded', JSON.stringify(next));
          return next;
        });
        break;
      }
    }
  }, [location.pathname]);

  return (
    <nav className="flex flex-col gap-0.5 p-3" data-testid="sidebar-nav">
      {NAV_GROUPS.map((group) => {
        if (group.module && moduleToggles && moduleToggles[group.module] === false) return null;

        const visibleItems = group.items.filter(item => hasAccess(userRole, item.key));
        if (visibleItems.length === 0) return null;

        // Single-item groups render as a direct link
        if (group.single && visibleItems.length === 1) {
          const item = visibleItems[0];
          const active = location.pathname === item.path;
          const unread = unreadByPath[item.path] || 0;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              data-testid={`nav-${item.path.slice(1)}`}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                active
                  ? 'bg-primary/10 text-primary font-semibold border-l-2 border-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <group.icon className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate">{group.label}</span>
              {unread > 0 && (
                <Badge variant="destructive" className="px-1.5 py-0 min-w-[18px] justify-center shrink-0 ring-1 ring-background" data-testid={`nav-unread-${item.path.slice(1)}`}>
                  {unread > 99 ? '99+' : unread}
                </Badge>
              )}
            </Link>
          );
        }

        const isExpanded = expanded[group.id] ?? false;
        const groupHasActive = visibleItems.some(item => location.pathname === item.path);
        const groupUnread = visibleItems.reduce((sum, item) => sum + (unreadByPath[item.path] || 0), 0);

        return (
          <div key={group.id} data-testid={`nav-group-${group.id}`}>
            <button
              onClick={() => toggleGroup(group.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                groupHasActive && !isExpanded
                  ? 'text-primary bg-primary/5'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
              data-testid={`nav-group-toggle-${group.id}`}
            >
              <group.icon className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate text-left">{group.label}</span>
              {groupUnread > 0 && !isExpanded && (
                <Badge variant="destructive" className="px-1.5 py-0 min-w-[18px] justify-center shrink-0 ring-1 ring-background" data-testid={`nav-group-unread-${group.id}`}>
                  {groupUnread > 99 ? '99+' : groupUnread}
                </Badge>
              )}
              {isExpanded
                ? <ChevronDown className="h-3.5 w-3.5 opacity-50" />
                : <ChevronRight className="h-3.5 w-3.5 opacity-50" />
              }
            </button>
            <div className={`overflow-hidden transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="ml-3 pl-3 border-l border-border/50 mt-0.5 mb-1 flex flex-col gap-0.5">
                {visibleItems.map((item) => {
                  const active = location.pathname === item.path;
                  const unread = unreadByPath[item.path] || 0;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={onNavigate}
                      data-testid={`nav-${item.path.slice(1)}`}
                      className={`flex items-center gap-3 px-3 py-1.5 rounded-md text-[13px] font-medium transition-all duration-150 ${
                        active
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="flex-1 min-w-0 truncate">{item.label}</span>
                      {unread > 0 && (
                        <Badge variant="destructive" className="px-1.5 py-0 min-w-[18px] justify-center shrink-0 ring-1 ring-background" data-testid={`nav-unread-${item.path.slice(1)}`}>
                          {unread > 99 ? '99+' : unread}
                        </Badge>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}

// Re-export for any consumers that imported ROLE_LABELS from Layout
export { ROLE_LABELS } from '../../lib/navConfig';
